import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { hasToken } from "../services/tokenVault.js";
import Button from "../components/ui/Button.jsx";
import Card from "../components/ui/Card.jsx";
import ProjectCard from "../components/ProjectCard.jsx";
import NewProjectModal from "../components/NewProjectModal.jsx";
import ResumeProjectModal from "../components/ResumeProjectModal.jsx";
import Skeleton from "../components/ui/Skeleton.jsx";
import { productLogout } from "../hooks/useProductMe.js";
import SettingsEntryLink from "../components/settings/SettingsEntryLink.jsx";
import { shouldSkipResumeModal } from "../utils/projectProvider.js";

export default function ProjectDashboard({ userEmail }) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [resumeProject, setResumeProject] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/projects", { credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Failed to load projects");
      setProjects(Array.isArray(data.projects) ? data.projects : []);
    } catch (e) {
      setError(e?.message || "Failed to load");
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSignOut = async () => {
    try {
      await productLogout();
    } finally {
      window.location.href = "/login";
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!projectId) return;
    const yes = window.confirm("Delete this project? This cannot be undone.");
    if (!yes) return;
    setDeleting(projectId);
    setError(null);
    try {
      const res = await fetch(`/api/projects/${encodeURIComponent(projectId)}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.error || "Failed to delete project");
      await load();
    } catch (e) {
      setError(e?.message || "Failed to delete project");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-base)" }}>
      <div style={{ maxWidth: "860px", margin: "0 auto", padding: "48px 24px" }}>
        <header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "32px",
          }}
        >
          <span
            style={{
              fontSize: "13px",
              fontWeight: 700,
              letterSpacing: "0.15em",
              color: "var(--accent)",
            }}
          >
            IDWIZ
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <span className="text-secondary" style={{ fontSize: "13px" }}>
              {userEmail || ""}
            </span>
            <SettingsEntryLink />
            <Button variant="ghost" size="sm" onClick={handleSignOut}>
              Sign out
            </Button>
          </div>
        </header>

        <section style={{ marginBottom: "24px" }}>
          <h1
            style={{
              fontSize: "28px",
              fontWeight: 600,
              color: "var(--text-primary)",
              marginBottom: "8px",
            }}
          >
            Your projects
          </h1>
          <p className="text-secondary" style={{ fontSize: "15px", maxWidth: "520px" }}>
            Each project tracks an identity provider deployment — assessment, roadmap, and consultation in one place.
          </p>
        </section>

        {error ? (
          <p className="text-critical" style={{ marginBottom: "16px" }}>
            {error}
          </p>
        ) : null}

        {!loading && projects.length > 0 ? (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "16px",
            }}
          >
            <Button variant="secondary" size="md" onClick={() => setShowNew(true)}>
              + New Project
            </Button>
          </div>
        ) : null}

        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  padding: "16px",
                  borderRadius: "8px",
                  border: "1px solid var(--border)",
                  background: "var(--bg-surface)",
                }}
              >
                <div style={{ marginBottom: "10px" }}>
                  <Skeleton height={18} width="45%" borderRadius={4} />
                </div>
                <div style={{ marginBottom: "8px" }}>
                  <Skeleton height={14} width="85%" borderRadius={4} />
                </div>
                <Skeleton height={14} width="60%" borderRadius={4} />
              </div>
            ))}
          </div>
        ) : projects.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 16px" }}>
            <div style={{ fontSize: "56px", marginBottom: "12px" }} aria-hidden>
              🔐
            </div>
            <h2 style={{ fontSize: "20px", fontWeight: 600, marginBottom: "8px" }}>
              Welcome to idwiz
            </h2>
            <p className="text-secondary" style={{ marginBottom: "24px" }}>
              Create your first project to connect Okta or Microsoft Entra and run assessments.
            </p>
            <Button variant="primary" size="lg" onClick={() => setShowNew(true)}>
              Create your first project
            </Button>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {[...projects]
              .sort((a, b) => {
                const tb = new Date(b?.updatedAt || 0).getTime();
                const ta = new Date(a?.updatedAt || 0).getTime();
                return tb - ta;
              })
              .map((p) => {
                const orgUrl = p.orgUrl || p.org?.url || "";
                const skipResume = shouldSkipResumeModal(p);
                return (
                  <ProjectCard
                    key={p.projectId}
                    project={p}
                    sessionActive={skipResume || Boolean(orgUrl && hasToken(orgUrl))}
                    onResume={() => {
                      if (skipResume) {
                        navigate(`/project/${encodeURIComponent(p.projectId)}`);
                        return;
                      }
                      if (orgUrl && hasToken(orgUrl)) {
                        navigate(`/project/${encodeURIComponent(p.projectId)}`, {
                          state: { reconnectBanner: orgUrl },
                        });
                        return;
                      }
                      setResumeProject(p);
                    }}
                    onView={() => navigate(`/project/${encodeURIComponent(p.projectId)}/report`)}
                    onRenamed={() => load()}
                    onDelete={
                      deleting === p.projectId
                        ? undefined
                        : () => handleDeleteProject(p.projectId)
                    }
                  />
                );
              })}
          </div>
        )}
      </div>

      {showNew ? (
        <NewProjectModal
          onClose={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            load();
          }}
        />
      ) : null}

      {resumeProject ? (
        <ResumeProjectModal
          projectSummary={resumeProject}
          onClose={() => setResumeProject(null)}
          onDone={() => {
            setResumeProject(null);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
